import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { SamlSpMetadataContext } from '../../contexts/saml-sp-metadata.context';
import { SamlIdpMetadataContext } from '../../contexts/saml-idp-metadata.context';
import { SamlSsoConfig, Timestamp } from '../../../shared/models/generated/ziroModels';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { ToastService } from '../../../shared/services/toast.service';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { SamlSsoConfigContext } from '../../contexts/saml-sso-config.context';
import { GlobalPropertiesContext } from '../../../shared/contexts/global-properties.context';
import { ZiroFormsUpdate } from '../../../forms/ziro-forms-models';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { finalize, map } from 'rxjs/operators';

@Component({
  selector: 'ziro-admin-saml',
  templateUrl: './saml.component.html',
  styleUrls: ['../../admin-page.scss'],
  providers: [SamlIdpMetadataContext, SamlSpMetadataContext],
  standalone: false,
})
export class SamlComponent implements OnInit, OnDestroy {
  private globalPropertiesContext = inject(GlobalPropertiesContext);
  private samlSsoConfigContext = inject(SamlSsoConfigContext);
  private samlSpMetadataContext = inject(SamlSpMetadataContext);
  private samlIdpMetadataContext = inject(SamlIdpMetadataContext);
  private toastService = inject(ToastService);
  private breadcrumbsService = inject(BreadcrumbsService);
  private bottomNavService = inject(BottomNavService);

  isHostedEnabled = false;
  private _subscriptions = new Subscription();
  isLoading = true;
  samlConfig: SamlSsoConfig = { ssoEnabled: false };
  samlSpMetaDataTimestamp: Timestamp = { timestamp: '' };
  samlIdpMetaDataTimestamp: Timestamp = { timestamp: '' };
  samlSpDate: Date = null;
  samlIdpDate: Date = null;
  samlSpGetMetadataXML: () => Observable<void>;
  samlIdpGetMetadataXML: () => Observable<void>;
  samlIdpUpload: (formData: FormData) => Observable<void>;
  isSubmitted = false;

  ziroIcons = ZiroIcons;
  buttonStyles = ButtonStyles;

  ngOnInit(): void {
    this.breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;admin.saml.title' }]);
    this.samlSpGetMetadataXML = this.samlSpMetadataContext.getMetadataXML;
    this.samlIdpGetMetadataXML = this.samlIdpMetadataContext.getMetadataXML;
    this.samlIdpUpload = this.samlIdpMetadataContext.uploadMetadata;

    const globalPropertiesObs = this.globalPropertiesContext.state$.pipe(
      map((globalProperties) => {
        this.isHostedEnabled = globalProperties.hostedEnabled;
      }),
    );

    const samlSPMetadataObs = this.samlSpMetadataContext.state$.pipe(
      map((timestamp: Timestamp) => {
        this.samlSpMetaDataTimestamp = timestamp;
        this.samlSpDate = timestamp.timestamp ? this._parseDate(timestamp) : null;
      }),
    );

    const samlIdpMetadataObs = this.samlIdpMetadataContext.state$.pipe(
      map((timestamp: Timestamp) => {
        this.samlIdpMetaDataTimestamp = timestamp;
        this.samlIdpDate = timestamp.timestamp ? this._parseDate(timestamp) : null;
      }),
    );

    const ssoEnabledContextObs = this.samlSsoConfigContext.state$.pipe(
      map((samlConfig) => {
        this.samlConfig = samlConfig;
      }),
    );

    const sub = combineLatest([
      globalPropertiesObs,
      samlSPMetadataObs,
      samlIdpMetadataObs,
      ssoEnabledContextObs,
    ]).subscribe(() => {
      this.isLoading = false;
      this._initBottomNav();
    });
    this._subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this.breadcrumbsService.clearBreadcrumbs();
    this._subscriptions.unsubscribe();
  }

  onEnabledToggled(): void {
    this._saveSsoConfig().subscribe();
  }

  onSsoTypeFormUpdate(update: ZiroFormsUpdate<SamlSsoConfig>): void {
    this.samlConfig = update.new;
  }

  private _saveSsoConfig(): Observable<void> {
    return this.samlSsoConfigContext.set(this.samlConfig).pipe(
      map(() => {
        this.toastService.push(
          ToastTypes.SUCCESS,
          this.ziroIcons.SAVE_SUCCESS,
          this.samlConfig.ssoEnabled ? 'tkey;admin.saml.toast.enabled.title' : 'tkey;admin.saml.toast.disabled.title',
          this.samlConfig.ssoEnabled
            ? 'tkey;admin.saml.toast.enabled.description'
            : 'tkey;admin.saml.toast.disabled.description',
        );
      }),
    );
  }

  private _initBottomNav(): void {
    if (this.isHostedEnabled) {
      this.bottomNavService.dispatch(
        new BottomNavUpdateButtonsList([
          {
            id: 'saml-sso-save-button',
            dataAutomation: 'saml-sso-save-button',
            label: 'tkey;global.button.save.text',
            buttonClass: ButtonStyles.PRIMARY,
            state: {
              pending: false,
              buttonDisableState: {
                disabled: false,
                tooltipKey: '',
              },
            },
            icon: ZiroIcons.SAVE_SUCCESS,
            type: ButtonTypes.BUTTON,
            cb: () => {
              this._onSaveButtonClicked();
            },
          },
        ]),
      );
    } else {
      // for workbench
      this.bottomNavService.dispatch(new BottomNavUpdateButtonsList([]));
    }
  }

  private _onSaveButtonClicked(): void {
    this.isSubmitted = true;
    if (this.samlConfig.ssoEnabled && !this.samlIdpMetaDataTimestamp.timestamp) {
      this.bottomNavService.dispatch(new BottomNavUpdateState({ hasValidationError: true }));
      return;
    }

    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({ id: 'saml-sso-save-button', state: { pending: true } }),
    );
    this._saveSsoConfig()
      .pipe(
        finalize(() => {
          this.bottomNavService.dispatch(
            new BottomNavUpdateButtonState({ id: 'saml-sso-save-button', state: { pending: false } }),
          );
        }),
      )
      .subscribe();
  }

  private _parseDate(timestamp: Timestamp): Date {
    const formattedDateArray = timestamp.timestamp.replace(/[-.]/g, ' ').split(' ');
    const formattedUtcDate = Date.UTC.apply(this, formattedDateArray);
    const formattedDate = new Date(formattedUtcDate);
    formattedDate.setMonth(formattedDate.getMonth() - 1);
    return formattedDate;
  }

  handleInvalidImportTypeError(): void {
    this.toastService.push(
      ToastTypes.ERROR,
      this.ziroIcons.DOWNLOAD,
      'tkey;admin.saml.toast.invalid.title',
      'tkey;admin.saml.toast.invalid.description',
    );
  }

  handleSuccessfulFileImport(): void {
    this.bottomNavService.dispatch(new BottomNavUpdateState({ hasValidationError: false }));
    this.toastService.push(
      ToastTypes.SUCCESS,
      this.ziroIcons.DOWNLOAD,
      'tkey;admin.saml.toast.success.title',
      'tkey;admin.saml.toast.success.description',
    );
  }
}
